/**
 * Tournament Access Control
 *
 * Centralized permission and access control logic for tournament features.
 * This file contains all the business rules for what actions are allowed
 * based on tournament state, user roles, and other conditions.
 */

import { TOURNAMENT_STATUS, GAME_STATUS, CHECKIN_STATUS } from './constants';

// ============================================================================
// ENTITY CONSTANTS - Define all tournament actions/features
// ============================================================================

export const TOURNAMENT_ENTITIES = {
  // Tournament-level actions
  TOURNAMENT_EDIT: 'TOURNAMENT_EDIT',
  TOURNAMENT_DELETE: 'TOURNAMENT_DELETE',
  TOURNAMENT_COMPLETE: 'TOURNAMENT_COMPLETE',

  // Registration actions
  REGISTRATION_ENABLE: 'REGISTRATION_ENABLE',
  REGISTRATION_WITHDRAW: 'REGISTRATION_WITHDRAW',

  // Schedule management
  SCHEDULE_CREATE: 'SCHEDULE_CREATE',
  SCHEDULE_EDIT: 'SCHEDULE_EDIT',
  SCHEDULE_DELETE: 'SCHEDULE_DELETE',
  SCHEDULE_REGENERATE: 'SCHEDULE_REGENERATE',

  // Round management
  ROUND_ADD: 'ROUND_ADD',
  ROUND_DELETE: 'ROUND_DELETE',

  // Match/Game management
  MATCH_ADD: 'MATCH_ADD',
  MATCH_EDIT: 'MATCH_EDIT',
  MATCH_DELETE: 'MATCH_DELETE',
  MATCH_SCHEDULE: 'MATCH_SCHEDULE',
  MATCH_RESCHEDULE: 'MATCH_RESCHEDULE',

  // Game actions
  GAME_CHECKIN: 'GAME_CHECKIN',
  GAME_START: 'GAME_START',
  GAME_LIVE: 'GAME_LIVE',
  GAME_COMPLETE: 'GAME_COMPLETE',
  GAME_EDIT_RESULT: 'GAME_EDIT_RESULT',

  // Participant management
  PARTICIPANT_EDIT: 'PARTICIPANT_EDIT',
  PARTICIPANT_REMOVE: 'PARTICIPANT_REMOVE',
};

// Re-export status constants from constants.js for backwards compatibility
export { TOURNAMENT_STATUS, GAME_STATUS, CHECKIN_STATUS };

// ============================================================================
// USER ROLE HELPERS
// ============================================================================

/**
 * Check if user is the tournament organizer
 */
export function isOrganizer(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  return tournamentVO.organizer_user_id === userId ||
         tournamentVO.organizerUserId === userId;
}

/**
 * Check if user is a scorer for the tournament
 */
export function isScorer(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  if (isOrganizer(userId, tournamentVO)) return true;
  // Check if user is a tournament participant with scorer role
  return tournamentVO.participants?.some(p =>
    (p.participant_id === userId || p.participantId === userId) && p.scorer === true
  ) ?? false;
}

/**
 * Check if user is a participant in the tournament
 */
function isParticipant(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  // Check if user is in participants list
  return tournamentVO.participants?.some(p =>
    p.participant_id === userId || p.participantId === userId
  );
}

/**
 * Check if user is a co-owner of the tournament
 */
export function isCoOwner(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  // Co-ownership is set via the `coowner` boolean on tournament_participants
  // (e.g. the tournament creator is auto-registered with coowner: true) —
  // participant_role === 'co-owner' is a secondary/legacy representation of
  // the same thing, so both are checked.
  return tournamentVO.participants?.some(p =>
    (p.participant_id === userId || p.participantId === userId) &&
    (p.coowner === true || p.coOwner === true ||
     p.participant_role === 'co-owner' || p.participantRole === 'co-owner')
  );
}

/**
 * Check if user can manage rounds/matches (add round, add match, edit
 * participants, delete, reorder). Creator, organizer/co-owner, and scorer
 * are the only roles allowed — plain participants and spectators are not.
 */
export function canManageMatches(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  return isOrganizer(userId, tournamentVO) || isCoOwner(userId, tournamentVO) || isScorer(userId, tournamentVO);
}

// ============================================================================
// TOURNAMENT STATE HELPERS
// ============================================================================

/**
 * Check if tournament is completed
 */
function isTournamentCompleted(tournamentVO) {
  return tournamentVO?.status === TOURNAMENT_STATUS.COMPLETED;
}

/**
 * Check if tournament is in progress
 */
function isTournamentInProgress(tournamentVO) {
  return tournamentVO?.status === TOURNAMENT_STATUS.IN_PROGRESS;
}

/**
 * Check if tournament has started (has games in progress or completed)
 */
function hasTournamentStarted(tournamentVO) {
  if (!tournamentVO?.rounds) return false;

  return tournamentVO.rounds.some(round =>
    round.games?.some(game =>
      game.status === GAME_STATUS.IN_PROGRESS ||
      game.status === GAME_STATUS.COMPLETED ||
      game.status === 'Completed' // Handle case variations
    )
  );
}

/**
 * Check if all rounds are completed
 */
function areAllRoundsCompleted(tournamentVO) {
  if (!tournamentVO?.rounds || tournamentVO.rounds.length === 0) {
    return false;
  }

  return tournamentVO.rounds.every(round =>
    round.games?.length > 0 && round.games.every(game =>
      game.status === GAME_STATUS.COMPLETED ||
      game.status === 'Completed' ||
      game.checkin_status === CHECKIN_STATUS.COMPLETED
    )
  );
}

/**
 * Check if game is completed
 */
function isGameCompleted(game) {
  if (!game) return false;
  return game.status === GAME_STATUS.COMPLETED ||
         game.status === 'Completed' ||
         game.checkin_status === CHECKIN_STATUS.COMPLETED;
}

/**
 * Check if game has started
 */
function hasGameStarted(game) {
  if (!game) return false;
  return game.status === GAME_STATUS.IN_PROGRESS ||
         game.checkin_status === CHECKIN_STATUS.LIVE;
}

/**
 * Check if registration deadline has passed
 */
function isRegistrationDeadlinePassed(tournamentVO) {
  if (!tournamentVO?.registration_deadline && !tournamentVO?.registrationDeadline) {
    return false;
  }

  const deadline = tournamentVO.registration_deadline || tournamentVO.registrationDeadline;
  return new Date(deadline) < new Date();
}

// ============================================================================
// MAIN ACCESS CONTROL FUNCTION
// ============================================================================

/**
 * Centralized access control function for tournament features
 *
 * @param {number|string} userId - Current user ID
 * @param {string} entity - Entity constant from TOURNAMENT_ENTITIES
 * @param {Object} tournamentVO - Tournament value object with all necessary state
 * @param {Object} context - Additional context (game, round, etc.)
 * @returns {boolean} - Whether the action is enabled/allowed
 *
 * @example
 * isEnabled(userId, TOURNAMENT_ENTITIES.REGISTRATION_ENABLE, tournament)
 * isEnabled(userId, TOURNAMENT_ENTITIES.GAME_START, tournament, { game })
 */
export function isEnabled(userId, entity, tournamentVO, context = {}) {
  // Basic validation
  if (!tournamentVO) return false;

  // If tournament is completed, most actions are disabled
  const tournamentCompleted = isTournamentCompleted(tournamentVO);

  switch (entity) {
    // ========================================================================
    // TOURNAMENT-LEVEL ACTIONS
    // ========================================================================

    case TOURNAMENT_ENTITIES.TOURNAMENT_EDIT:
      // Organizer can edit tournament only before it starts
      return isOrganizer(userId, tournamentVO) && !tournamentCompleted && !isTournamentInProgress(tournamentVO);

    case TOURNAMENT_ENTITIES.TOURNAMENT_DELETE:
      // Organizer can delete a tournament at any point — any status, any
      // number of participants/games played. Backend cascades everything
      // tournament-specific while preserving game history.
      return isOrganizer(userId, tournamentVO);

    case TOURNAMENT_ENTITIES.TOURNAMENT_COMPLETE:
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             areAllRoundsCompleted(tournamentVO);

    // ========================================================================
    // REGISTRATION ACTIONS
    // ========================================================================

    case TOURNAMENT_ENTITIES.REGISTRATION_ENABLE:
      return !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO) &&
             !isRegistrationDeadlinePassed(tournamentVO);

    case TOURNAMENT_ENTITIES.REGISTRATION_WITHDRAW:
      return !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO) &&
             !hasTournamentStarted(tournamentVO) &&
             isParticipant(userId, tournamentVO);

    // ========================================================================
    // SCHEDULE MANAGEMENT
    // ========================================================================

    case TOURNAMENT_ENTITIES.SCHEDULE_CREATE:
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO);

    case TOURNAMENT_ENTITIES.SCHEDULE_EDIT:
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO);

    case TOURNAMENT_ENTITIES.SCHEDULE_DELETE:
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO) &&
             !hasTournamentStarted(tournamentVO);

    case TOURNAMENT_ENTITIES.SCHEDULE_REGENERATE:
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO) &&
             !hasTournamentStarted(tournamentVO);

    // ========================================================================
    // ROUND MANAGEMENT
    // ========================================================================

    case TOURNAMENT_ENTITIES.ROUND_ADD:
      // Adding a new round never touches games already in progress
      // elsewhere in the tournament, so it only needs to be blocked once
      // the tournament itself is completed.
      return canManageMatches(userId, tournamentVO) && !tournamentCompleted;

    case TOURNAMENT_ENTITIES.ROUND_DELETE:
      const round = context.round;
      if (!round) return false;

      const roundHasStartedGames = round.games?.some(game =>
        hasGameStarted(game) || isGameCompleted(game)
      );

      return canManageMatches(userId, tournamentVO) &&
             !tournamentCompleted &&
             !roundHasStartedGames;

    // ========================================================================
    // MATCH/GAME MANAGEMENT
    // ========================================================================

    case TOURNAMENT_ENTITIES.MATCH_ADD:
      // Same reasoning as ROUND_ADD — appending a new match doesn't touch
      // any match that's already started.
      return canManageMatches(userId, tournamentVO) && !tournamentCompleted;

    case TOURNAMENT_ENTITIES.MATCH_EDIT:
      const matchForEdit = context.game || context.match;
      if (!matchForEdit) return false;

      return canManageMatches(userId, tournamentVO) &&
             !tournamentCompleted &&
             !hasGameStarted(matchForEdit) &&
             !isGameCompleted(matchForEdit);

    case TOURNAMENT_ENTITIES.MATCH_DELETE:
      const matchForDelete = context.game || context.match;
      if (!matchForDelete) return false;

      return canManageMatches(userId, tournamentVO) &&
             !tournamentCompleted &&
             !hasGameStarted(matchForDelete) &&
             !isGameCompleted(matchForDelete);

    case TOURNAMENT_ENTITIES.MATCH_SCHEDULE:
    case TOURNAMENT_ENTITIES.MATCH_RESCHEDULE:
      const matchForSchedule = context.game || context.match;
      if (!matchForSchedule) return false;

      return canManageMatches(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isGameCompleted(matchForSchedule);

    // ========================================================================
    // GAME ACTIONS
    // ========================================================================

    case TOURNAMENT_ENTITIES.GAME_CHECKIN:
      const gameForCheckin = context.game;
      if (!gameForCheckin) return false;

      return !tournamentCompleted &&
             !isGameCompleted(gameForCheckin) &&
             gameForCheckin.checkin_status !== CHECKIN_STATUS.LIVE;

    case TOURNAMENT_ENTITIES.GAME_START:
      const gameForStart = context.game;
      if (!gameForStart) return false;

      return isScorer(userId, tournamentVO) &&
             !tournamentCompleted &&
             isTournamentInProgress(tournamentVO) &&
             !isGameCompleted(gameForStart) &&
             gameForStart.checkin_status === CHECKIN_STATUS.READY;

    case TOURNAMENT_ENTITIES.GAME_LIVE:
      const gameForLive = context.game;
      if (!gameForLive) return false;

      // Can go live if game has started and has an event scheduled
      return !tournamentCompleted &&
             !isGameCompleted(gameForLive) &&
             gameForLive.checkin_status === CHECKIN_STATUS.LIVE &&
             gameForLive.event_id != null;

    case TOURNAMENT_ENTITIES.GAME_COMPLETE:
      const gameForComplete = context.game;
      if (!gameForComplete) return false;

      return isScorer(userId, tournamentVO) &&
             !tournamentCompleted &&
             hasGameStarted(gameForComplete) &&
             !isGameCompleted(gameForComplete);

    case TOURNAMENT_ENTITIES.GAME_EDIT_RESULT:
      const gameForEditResult = context.game;
      if (!gameForEditResult) return false;

      // Can edit results only if tournament not completed
      // Individual game can be completed, but tournament must be open for edits
      return isScorer(userId, tournamentVO) && !tournamentCompleted;

    // ========================================================================
    // PARTICIPANT MANAGEMENT
    // ========================================================================

    case TOURNAMENT_ENTITIES.PARTICIPANT_EDIT:
      const gameForParticipantEdit = context.game;
      if (!gameForParticipantEdit) return false;

      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !hasGameStarted(gameForParticipantEdit) &&
             !isGameCompleted(gameForParticipantEdit);

    case TOURNAMENT_ENTITIES.PARTICIPANT_REMOVE:
      // Organizer can remove participants before tournament starts or completes
      return isOrganizer(userId, tournamentVO) &&
             !tournamentCompleted &&
             !isTournamentInProgress(tournamentVO);

    // ========================================================================
    // DEFAULT
    // ========================================================================

    default:
      console.warn(`Unknown tournament entity: ${entity}`);
      return false;
  }
}

// ============================================================================
// CONVENIENCE HELPER FUNCTIONS
// ============================================================================

/**
 * Check if any editing is allowed for the tournament
 */
export function canEditTournament(userId, tournamentVO) {
  return isEnabled(userId, TOURNAMENT_ENTITIES.TOURNAMENT_EDIT, tournamentVO);
}

/**
 * Check if registration is open
 */
export function canRegister(tournamentVO) {
  return isEnabled(null, TOURNAMENT_ENTITIES.REGISTRATION_ENABLE, tournamentVO);
}

/**
 * Check if user can manage the schedule
 * Both organizers and co-owners can manage the schedule
 */
export function canManageSchedule(userId, tournamentVO) {
  if (!userId || !tournamentVO) return false;
  return isOrganizer(userId, tournamentVO) || isCoOwner(userId, tournamentVO);
}

/**
 * Check if user can score games
 */
export function canScoreGames(userId, tournamentVO) {
  return isScorer(userId, tournamentVO) && !isTournamentCompleted(tournamentVO);
}

/**
 * Get a user-friendly reason why an action is disabled
 */
export function getDisabledReason(entity, tournamentVO, context = {}) {
  if (isTournamentCompleted(tournamentVO)) {
    return 'Tournament has been completed';
  }

  if (isTournamentInProgress(tournamentVO)) {
    switch (entity) {
      case TOURNAMENT_ENTITIES.TOURNAMENT_EDIT:
        return 'Tournament is in progress. Editing is locked';
      case TOURNAMENT_ENTITIES.REGISTRATION_ENABLE:
        return 'Tournament has started. Registration is locked';
      case TOURNAMENT_ENTITIES.REGISTRATION_WITHDRAW:
        return 'Tournament has started. Withdrawals are locked';
      case TOURNAMENT_ENTITIES.SCHEDULE_CREATE:
      case TOURNAMENT_ENTITIES.SCHEDULE_EDIT:
      case TOURNAMENT_ENTITIES.SCHEDULE_DELETE:
      case TOURNAMENT_ENTITIES.SCHEDULE_REGENERATE:
        return 'Tournament is in progress. Schedule is locked';
      case TOURNAMENT_ENTITIES.PARTICIPANT_REMOVE:
        return 'Tournament is in progress. Cannot remove participants';
      // GAME_START and GAME_LIVE require tournament to be in progress — fall through to specific checks below
      case TOURNAMENT_ENTITIES.GAME_START:
      case TOURNAMENT_ENTITIES.GAME_LIVE:
        break;
      default:
        return 'Tournament is in progress';
    }
  }

  switch (entity) {
    case TOURNAMENT_ENTITIES.REGISTRATION_ENABLE:
      if (isRegistrationDeadlinePassed(tournamentVO)) {
        return 'Registration deadline has passed';
      }
      break;

    case TOURNAMENT_ENTITIES.SCHEDULE_DELETE:
    case TOURNAMENT_ENTITIES.SCHEDULE_REGENERATE:
      if (hasTournamentStarted(tournamentVO)) {
        return 'Tournament has already started';
      }
      break;

    case TOURNAMENT_ENTITIES.GAME_START:
      const game = context.game;
      if (!isTournamentInProgress(tournamentVO)) {
        return 'Tournament must be started first';
      }
      if (game?.checkin_status !== CHECKIN_STATUS.READY) {
        return 'Both participants must check in first';
      }
      break;

    case TOURNAMENT_ENTITIES.GAME_LIVE:
      const gameForLive = context.game;
      if (!gameForLive?.event_id) {
        return 'Please schedule the game first';
      }
      break;

    default:
      return 'Action not available';
  }

  return 'Action not available';
}

export default {
  TOURNAMENT_ENTITIES,
  TOURNAMENT_STATUS,
  GAME_STATUS,
  CHECKIN_STATUS,
  isEnabled,
  isOrganizer,
  isCoOwner,
  canManageMatches,
  canEditTournament,
  canRegister,
  canManageSchedule,
  canScoreGames,
  getDisabledReason,
};
